import { useQuery, useMutation } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import ls from 'local-storage';

const fetchActivePackages = async (_) => await Axios.get('/packages/getactive');

const useActivePackages = () => {
    return useQuery({
        queryKey: ['activepackages'],
        queryFn: fetchActivePackages,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const getActivePackage = (ID) => {
    const packages = queryClient.getQueryData(['activepackages']);
    if (packages) {
        const p = packages.find((p) => p.ID === ID);
        if (p) {
            return p;
        }
    }
    return null;
};

const getActivePackages = () => {
    const packages = queryClient.getQueryData(['activepackages']) ? queryClient.getQueryData(['activepackages']) : [];
    return packages;
};

const prefetchActivePackages = () => {
    queryClient.prefetchQuery(['activepackages'], () => fetchActivePackages(), {
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const forceReloadActivePackages = () => queryClient.invalidateQueries({ queryKey: ['activepackages'] });

const onAddPackage = ({ data }) => Axios.post(`/packages/create`, data);

const useAddPackage = () => {
    return useMutation({
        mutationFn: onAddPackage,
        onSuccess: (data, variables) => {
            ls.set('nw_config_latest_package', data.ID);
            //forceReloadActivePackages();
        },
    });
};

const onClonePackage = ({ data }) => Axios.post(`/packages/clone`, data);

const useClonePackage = () => {
    return useMutation({
        mutationFn: onClonePackage,
        onSuccess: (data, variables) => {
            ls.set('nw_config_latest_package', data);
            forceReloadActivePackages();
            return data;
        },
    });
};

const onUpdatePackage = ({ data }) => Axios.put(`/packages`, data);

const useUpdatePackage = () => {
    return useMutation({
        mutationFn: onUpdatePackage,
        onSuccess: (data, variables) => {
            ls.set('nw_config_latest_package', data.ID);
            //forceReloadActivePackages();
        },
    });
};

const onDeletePackage = ({ PackageID }) => Axios.delete(`/packages/${PackageID}`);

const useDeletePackage = () => {
    return useMutation({
        mutationFn: onDeletePackage,
        onSuccess: (data, variables) => {
            ls.set('nw_config_latest_package', null);
            //forceReloadActivePackages();
        },
    });
};

const onAddPlaylistToPackage = ({ data }) => Axios.post('/PackagePlaylists', data);

const useAddPlaylistToPackage = () => {
    return useMutation({
        mutationFn: onAddPlaylistToPackage,
        onSuccess: (data, variables) => {
            if (variables.isActivePackage) {
                ls.set('nw_config_latest_package', variables.data.packageID);
            }
            if (variables.data.packageID) {
                forceReloadPackage(variables.data.packageID);
            }
            //forceReloadActivePackages();
        },
    });
};

const onDeletePlaylistFromPackage = ({ packagePlaylistId }) => Axios.delete(`/PackagePlaylists/${packagePlaylistId}`);

const useDeletePlaylistFromActivePackage = () => {
    return useMutation({
        mutationFn: onDeletePlaylistFromPackage,
        onSuccess: (data, variables) => {
            if (variables.isActivePackage) {
                ls.set('nw_config_latest_package', variables.packageID);
            }
            if (variables.packageID) {
                forceReloadPackage(variables.packageID);
            }
            //forceReloadActivePackages();
        },
    });
};

const useDeletePlaylistFromPackage = () => {
    return useMutation({
        mutationFn: onDeletePlaylistFromPackage,
        onSuccess: (data, variables) => {
            if (variables.isActivePackage) {
                ls.set('nw_config_latest_package', variables.packageID);
                //forceReloadActivePackages();
            }
            forceReloadPackage(variables.packageID);
        },
    });
};

const onGetPackageByID = (ID) => Axios.get(`/packages/${ID}`);

const usePackage = (ID) => {
    return useQuery({
        queryKey: ['package', ID],
        queryFn: () => onGetPackageByID(ID),
        staleTime: 10000,
        cacheTime: 10000,
        retry: false,
        enabled: ID ? true : false,
    });
};

const forceReloadPackage = (ID) => {
    queryClient.invalidateQueries({ queryKey: ['package', ID] });
    //forceReloadActivePackages()
};

const onFullSearchPackages = (filters) => Axios.post('/packages/search', { ...filters, sentOnly: true });

const useFullSearchPackages = (filters) => {
    return useQuery({
        queryKey: ['packages-FullSearch'],
        queryFn: () => onFullSearchPackages(filters),
        refetchOnWindowFocus: false,
        staleTime: 300000,
        cacheTime: Infinity,
        retry: false,
    });
};

const forceReloadFullSearchPackages = () => {
    queryClient.invalidateQueries({ queryKey: ['packages-FullSearch'] });
};

const refetchFullSearchPackages = async (filters) => {
    try {
        await queryClient.fetchQuery({
            queryKey: ['packages-FullSearch'],
            queryFn: () => onFullSearchPackages(filters),
        });
    } catch (error) {
        console.error(error);
    }
};

export {
    useActivePackages,
    forceReloadActivePackages,
    getActivePackage,
    getActivePackages,
    prefetchActivePackages,
    useAddPackage,
    useClonePackage,
    useUpdatePackage,
    useDeletePackage,
    useAddPlaylistToPackage,
    useDeletePlaylistFromPackage,
    useDeletePlaylistFromActivePackage,
    refetchFullSearchPackages,
    usePackage,
    forceReloadPackage,
    useFullSearchPackages,
    forceReloadFullSearchPackages,
};
